'use client';

import { c as _c } from "react/compiler-runtime";
import { Stack } from '@mui/material';
import type { Condition } from '@repo/api-gw-sdk';
import { ConditionPanelPopover } from '@/components/conditions/conditionPanelPopover';
import { ConditionSelectionPanel } from '@/components/conditions/conditionSelectionPanel';
import { useConditionPanel } from '@/components/conditions/useConditionPanel';
import { ControlIndicator } from '@/components/controls/controlIndicator';
import { CheckboxTreeFilter } from '@/components/filters/checkboxTreeFilter';
import { ACTION_ITEMS_PROPERTY } from '@/components/queryBuilder/propertyNames';
import { ControlsSeverity } from '@/data/inventory/data';
import { conditionValues, findValueCondition, type CompanionConditionComponentProps } from '@/types/advanceFilter';
import { ACTION_ITEM_OPTIONS, actionItemLabelOf, withActionItems } from './actionItemTypes';
import type { AlertLevel } from './alertsModel';
const ACTION_ITEM_LEVELS: AlertLevel[] = ['high', 'medium', 'low'];
const ACTION_ITEM_GROUPS = ACTION_ITEM_LEVELS.map(level => ({
  level,
  title: `${ControlsSeverity[level].title} severity`,
  color: ControlsSeverity[level].color,
  items: ACTION_ITEM_OPTIONS.filter(option => option.level === level)
})).filter(group => group.items.length);
const actionItemIdsOf = (level: string) => ACTION_ITEM_GROUPS.find(group => group.level === level)?.items.map(item => item.id) ?? [];

// The API has no severity for an action item, so a severity row stands for every action item of that severity
// and is ticked once all of them are.
const ActionItemsFilter = t0 => {
  const $ = _c(19);
  const {
    conditions,
    onChange
  } = t0;
  let T0;
  let onChildToggle;
  let onGroupToggle;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== conditions || $[1] !== onChange) {
    const selected = conditionValues(findValueCondition(conditions, ACTION_ITEMS_PROPERTY));
    const isLevelSelected = level => actionItemIdsOf(level).every(id => selected.includes(id));
    onGroupToggle = level_0 => {
      const ids = actionItemIdsOf(level_0);
      const rest = selected.filter(id_0 => !ids.includes(id_0));
      onChange(withActionItems(conditions, isLevelSelected(level_0) ? rest : [...rest, ...ids]));
    };
    onChildToggle = id_1 => onChange(withActionItems(conditions, selected.includes(id_1) ? selected.filter(value => value !== id_1) : [...selected, id_1]));
    T0 = CheckboxTreeFilter;
    t1 = "filter-action-item-severity-";
    t2 = "filter-action-item-";
    t3 = true;
    t4 = ACTION_ITEM_GROUPS.map(group => ({
      id: group.level,
      label: group.title,
      selected: isLevelSelected(group.level),
      adornment: <ControlIndicator backgroundColor={group.color} />,
      children: group.items.map(item => ({
        id: item.id,
        label: item.name,
        selected: selected.includes(item.id),
        adornment: <ControlIndicator backgroundColor={group.color} />
      }))
    }));
    $[0] = conditions;
    $[1] = onChange;
    $[2] = T0;
    $[3] = onChildToggle;
    $[4] = onGroupToggle;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    T0 = $[2];
    onChildToggle = $[3];
    onGroupToggle = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[9] !== onChildToggle) {
    t5 = (_, id_2) => onChildToggle(id_2);
    $[9] = onChildToggle;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== T0 || $[12] !== onGroupToggle || $[13] !== t1 || $[14] !== t2 || $[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <T0 groupTestIdPrefix={t1} childTestIdPrefix={t2} wrapChildLabels={t3} groups={t4} onGroupToggle={onGroupToggle} onChildToggle={t5} />;
    $[11] = T0;
    $[12] = onGroupToggle;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  return t6;
};
export const ActionItemsCondition = t0 => {
  const $ = _c(12);
  const {
    conditions,
    onChange,
    panelHost
  } = t0;
  const panel = useConditionPanel(panelHost);
  let t1;
  if ($[0] !== conditions || $[1] !== onChange) {
    t1 = <Stack className="overflow-y-auto px-[24px]"><ActionItemsFilter conditions={conditions} onChange={onChange} /></Stack>;
    $[0] = conditions;
    $[1] = onChange;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== conditions || $[4] !== onChange || $[5] !== t1) {
    t2 = <Stack className="w-[308px]"><ConditionSelectionPanel conditions={conditions} onConditionsChange={onChange} entityName="action item" labelOf={_temp}>{t1}</ConditionSelectionPanel></Stack>;
    $[3] = conditions;
    $[4] = onChange;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== panel.anchorEl || $[8] !== panel.close || $[9] !== panel.isOpen || $[10] !== t2) {
    t3 = <ConditionPanelPopover open={panel.isOpen} anchorEl={panel.anchorEl} onClose={panel.close}>{t2}</ConditionPanelPopover>;
    $[7] = panel.anchorEl;
    $[8] = panel.close;
    $[9] = panel.isOpen;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
function _temp(t0) {
  const {
    value
  } = t0;
  return actionItemLabelOf(value);
}